import React from 'react';
import { Lead } from '../types';
import { BarChart3, MapPin, Globe } from 'lucide-react';
interface LeadStatsProps {
  leads: Lead[];
}
function countBy<T extends string>(items: T[]): Record<T, number> {
  return items.reduce((acc, item) => {
    acc[item] = (acc[item] || 0) + 1;
    return acc;
  }, {} as Record<T, number>);
}
export function LeadStats({
  leads
}: LeadStatsProps) {
  const sourceCounts = countBy(leads.map(l => l.source));
  const stateCounts = countBy(leads.map(l => l.state));
  const sortedSources = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]);
  const sortedStates = Object.entries(stateCounts).sort((a, b) => b[1] - a[1]);
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-id="element-116">
      <div className="px-6 py-5 border-b border-slate-200 bg-slate-50/50 flex items-center gap-2" data-id="element-117">
        <BarChart3 className="w-5 h-5 text-slate-500" data-id="element-118" />
        <div data-id="element-119">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-120">
            Lead Breakdown
          </h2>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-121">
            {leads.length.toLocaleString()} leads matching your filters
          </p>
        </div>
      </div>

      {leads.length === 0 ? <div className="p-12 text-center text-slate-500" data-id="element-122">
          No leads match the current filters.
        </div> : <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-123">
          {/* Sources */}
          <div data-id="element-124">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3" data-id="element-125">
              <Globe className="w-3.5 h-3.5" data-id="element-126" /> By Source
            </div>
            <div className="space-y-2" data-id="element-127">
              {sortedSources.map(([source, count]) => {
            const pct = count / leads.length * 100;
            return <div key={source} data-id="element-128">
                    <div className="flex justify-between items-baseline mb-1" data-id="element-129">
                      <span className="text-sm text-slate-700" data-id="element-130">{source}</span>
                      <span className="text-sm tabular-nums" data-id="element-131">
                        <span className="font-semibold text-slate-900" data-id="element-132">
                          {count.toLocaleString()}
                        </span>
                        <span className="text-slate-400 text-xs ml-1.5" data-id="element-133">
                          {pct.toFixed(1)}%
                        </span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-134">
                      <div className="h-full bg-indigo-500 rounded-full transition-all" style={{
                  width: `${pct}%`
                }} data-id="element-135" />
                    </div>
                  </div>;
          })}
            </div>
          </div>

          {/* States */}
          <div data-id="element-136">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3" data-id="element-137">
              <MapPin className="w-3.5 h-3.5" data-id="element-138" /> By State
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-id="element-139">
              {sortedStates.map(([state, count]) => {
            const pct = count / leads.length * 100;
            return <div key={state} data-id="element-140">
                    <div className="flex justify-between items-baseline mb-1" data-id="element-141">
                      <span className="text-sm font-mono font-semibold text-slate-700" data-id="element-142">
                        {state}
                      </span>
                      <span className="text-sm tabular-nums" data-id="element-143">
                        <span className="font-semibold text-slate-900" data-id="element-144">
                          {count.toLocaleString()}
                        </span>
                        <span className="text-slate-400 text-xs ml-1.5" data-id="element-145">
                          {pct.toFixed(1)}%
                        </span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-146">
                      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{
                  width: `${pct}%`
                }} data-id="element-147" />
                    </div>
                  </div>;
          })}
            </div>
          </div>
        </div>}
    </div>;
}